import React, { useEffect, useMemo, useState } from 'react';
import { transactionTemplatesService } from '../../services/transactionTemplates.service';
import { countTemplate } from '../../utils/transactionTemplates';
import './TransactionTemplatePicker.css';

const SALE_TYPES = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'PROBATE', label: 'Probate' },
  { value: 'REO', label: 'REO' },
  { value: 'TRUST', label: 'Trust' },
];

// Dropdown group per template owner (backend /templates/available keys).
const GROUP_LABELS = {
  MINE: 'My templates',
  BROKER: 'Broker templates',
  BROKERAGE: 'Brokerage templates',
  PRIMARY_AGENT: "Primary agent's templates",
};

/**
 * Optional template choice on the create-transaction pages, plus the Sale type
 * the template's TDS/NHD exemptions depend on. Renders with the host page's
 * form classes (classPrefix "listing" or "purchase").
 */
const TransactionTemplatePicker = ({
  classPrefix, side, agentState, primaryAgentId, templateId, onTemplateChange, saleType, onSaleTypeChange,
  hideSaleType = false, allowNone = true,
}) => {
  const [rialtyTemplates, setRialtyTemplates] = useState([]);
  const [customGroups, setCustomGroups] = useState([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    // Rialty templates, plus custom ones: mine, my Broker's and Brokerage's,
    // and (for a TC) the selected primary agent's.
    const filters = { state: agentState || undefined, side };
    Promise.all([
      transactionTemplatesService.getSuggestedTemplates(filters),
      transactionTemplatesService.getAvailableTemplates({ ...filters, primaryAgentId }).catch(() => []),
    ])
      .then(([rialty, groups]) => {
        if (cancelled) return;
        setRialtyTemplates(rialty);
        setCustomGroups(groups.filter((g) => g.templates?.length > 0));
      })
      .catch((error) => {
        console.error('[TransactionTemplatePicker] Failed to load templates', error);
        if (!cancelled) { setRialtyTemplates([]); setCustomGroups([]); setLoadError(true); }
      });
    return () => { cancelled = true; };
  }, [agentState, side, primaryAgentId]);

  const templates = useMemo(
    () => [...rialtyTemplates, ...customGroups.flatMap((g) => g.templates)],
    [rialtyTemplates, customGroups],
  );

  // A TC switching to an agent from another state can leave a template that no longer fits.
  useEffect(() => {
    if (templateId && templates.length > 0 && !templates.some((t) => t.id === templateId)) {
      onTemplateChange('');
    }
  }, [templates, templateId, onTemplateChange]);

  const selected = templates.find((t) => t.id === templateId);
  const counts = selected ? countTemplate(selected) : null;

  return (
    <div className="tpl-picker">
      <div className={`${classPrefix}-form-group`}>
        <label className={`${classPrefix}-form-label`} htmlFor="tpl-picker-template">
          Template {allowNone && <span className="tpl-picker-optional">(optional)</span>}
        </label>
        <select
          id="tpl-picker-template"
          className={`${classPrefix}-form-input ${classPrefix}-form-select`}
          value={templateId}
          onChange={(e) => onTemplateChange(e.target.value)}
          disabled={templates.length === 0}
        >
          <option value="">{templates.length === 0 ? 'No templates available' : allowNone ? 'No template' : 'Select a template'}</option>
          {rialtyTemplates.length > 0 && (
            <optgroup label="Rialty templates">
              {rialtyTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </optgroup>
          )}
          {customGroups.map((group) => (
            <optgroup key={group.key} label={GROUP_LABELS[group.key] || 'Templates'}>
              {group.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </optgroup>
          ))}
        </select>
        {loadError && <span className="tpl-picker-hint">Templates couldn't be loaded. You can still create the transaction.</span>}
        {counts && (
          <span className="tpl-picker-hint">
            Adds {counts.documentCount} documents and {counts.partyCount} parties to this transaction.
          </span>
        )}
      </div>

      {/* Sale type only matters for templates with sale-type exemptions (CA: NHD). */}
      {!hideSaleType && selected && selected.items?.some((i) => i.condition?.excludeSaleTypes?.length) && (
        <div className={`${classPrefix}-form-group`}>
          <label className={`${classPrefix}-form-label`} htmlFor="tpl-picker-sale-type">Sale type</label>
          <select
            id="tpl-picker-sale-type"
            className={`${classPrefix}-form-input ${classPrefix}-form-select`}
            value={saleType}
            onChange={(e) => onSaleTypeChange(e.target.value)}
          >
            {SALE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <span className="tpl-picker-hint">Decides whether NHD is needed for this sale.</span>
        </div>
      )}
    </div>
  );
};

export default TransactionTemplatePicker;
